import { getIn } from 'formik';
import * as Yup from 'yup';
import {
  aopDeductibleIsAlwaysPercent,
  noWindHailDeductibleStates,
  paymentMethod,
  windHailDeductibleCanVaryByCounty
} from '@ourbranch/lookups';

import { hasNewCoApplicant } from 'customer/components/policy/details/helpers';

import { validAffinityCode, validHomePurchaseDate, addressBlock } from 'common/helpers/yup-helpers';

// #35617: minimum home Coverage A an authorized limit override may set (min $1,000, no maximum).
const HOME_COVERAGE_LIMIT_OVERRIDE_FLOOR = 1000;
import { umbrellaValidationSchema } from './umbrella.validation-schema';
import { connectedHomeValidationSchema } from './connected-home.validation-schema';

Yup.addMethod(Yup.mixed, 'validAffinityCode', validAffinityCode);
Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);

export const homeValidationSchema = ({
  policies,
  policy,
  isAdvancedConnectedHome,
  savedSegment,
  session,
  affinityLookups,
  autoPolicyDetails
}) => {
  const baseSchema = Yup.object().shape({
    home: Yup.object().shape({
      purchaseDate: Yup.string().validHomePurchaseDate('home.purchaseDate', false),
      roofYear: Yup.number()
        .test('roofYearMin', 'Roof year must be higher than 1899', (value) => value >= 1899)
        .test(
          'roofYearMax',
          'Roof year cannot be greater than current year',
          (value) => value <= new Date().getUTCFullYear()
        ),
      yearBuilt: Yup.number().test('yearBuiltMin', 'Year built must be higher than 1899', function (value) {
        const state = this.options.context.state;
        if (['KY', 'MI', 'MO'].includes(state)) {
          return true;
        }
        return value >= 1900;
      }),
      sqFt: Yup.number()
        .positive()
        .integer()
        .test('sqFt-must-be-greater-than-0', 'sqFt must be a positive number', (val) => {
          return Number(val) && val > 0;
        }),
      mortgageDetails: Yup.array(
        Yup.object().shape({
          mortgageHolderName: Yup.string()
            .trim()
            .required('Required. If trying to remove this mortgage, use the Trash button.'),
          mortgageHolderAddress: addressBlock('Mortgage holder address'),
          mortgageHolderEmail: Yup.string().trim().email('Please use a valid email').nullable(),
          mortgageHolderFax: Yup.string().trim().nullable(),
          loanNumber: Yup.string().trim().required('Mortgage loan number is required')
        })
      )
        .nullable()
        .test(
          'atLeastOneRequired',
          'At least one mortgage details required because payment method is mortgage',
          (mortgageList) => {
            if (policy.paymentMethod === paymentMethod.Escrow) {
              return mortgageList?.length > 0;
            }
            return true; // force pass because we don't care about length of mortgage list if not paying by mortgage
          }
        ),
      homeLocation: addressBlock('Home address')
    }),
    homeCoverage: Yup.object().shape({
      coverageX: Yup.number().test(
        'umbrellaLiabilityMinimum',
        'Umbrella coverage requires at least $300K in homeowner’s liability.',
        function (value) {
          if (!this.options.context?.includeUmbrella) {
            return true;
          }
          return (value || 0) >= 300000;
        }
      ),
      coverageA: Yup.number().test(
        'coverageA',
        'Error, dwelling limit value cannot be less than estimated cost to rebuild home.',
        function (currentCoverageA) {
          // #35617: an authorized Coverage A limit override locks the corrected limit and bypasses the
          // "not below rebuild cost" floor, enforcing only the $1,000 hard floor (min $1,000, no max).
          if (getIn(this.parent, 'limitOverride')) {
            return currentCoverageA == null || currentCoverageA >= HOME_COVERAGE_LIMIT_OVERRIDE_FLOOR;
          }
          const { home } = this.options.context;
          return home.replacementCostEstimate.total <= currentCoverageA;
        }
      ),
      // #35617: authorized Coverage A limit override. When on, coverageA bypasses the rebuild-cost floor
      // (above) and a reason becomes required.
      limitOverride: Yup.boolean().nullable(),
      limitOverrideBy: Yup.string().nullable(),
      limitOverrideAt: Yup.string().nullable(),
      limitOverrideRemovedBy: Yup.string().nullable(),
      limitOverrideRemovedAt: Yup.string().nullable(),
      limitOverrideReason: Yup.string()
        .nullable()
        .when('limitOverride', {
          is: (value) => !!value,
          then: (schema) => schema.trim().required('Please enter a reason for the coverage limit override'),
          otherwise: (schema) => schema.nullable()
        }),
      coverageCourseOfConstruction: Yup.boolean(),
      coverageFairRental: Yup.boolean().when('coverageCourseOfConstruction', {
        is: true,
        then: Yup.boolean().test('coverageFairRental', 'Active construction. Set to inactive', (val) => !val),
        otherwise: Yup.boolean().required()
      }),
      coverageBCPctOfA: Yup.number().when('coverageCourseOfConstruction', {
        is: true,
        then: Yup.number().test('coverageBCPctOfA', 'Active construction. Set to 0', (val) => val === 0),
        otherwise: Yup.number().required()
      }),
      coverageIBSC: Yup.number().when('coverageCourseOfConstruction', {
        is: true,
        then: Yup.number().test('coverageIBSC', 'Active construction. Must be < 20%', (val) => val < 20),
        otherwise: Yup.number()
      }),
      coverageDC: Yup.boolean().when('coverageCourseOfConstruction', {
        is: true,
        then: Yup.boolean().test('coverageDC', 'Active construction. Set to inactive', (val) => !val),
        otherwise: Yup.boolean()
      }),
      coverageWaterBackup: Yup.number().when('coverageCourseOfConstruction', {
        is: true,
        then: Yup.number().test('coverageWaterBackup', 'Active construction. Set to 0', (val) => val < 20),
        otherwise: Yup.number()
      }),
      deductibleWindHail: Yup.number()
        .nullable()
        .test(
          'CompareToAllOtherPerils',
          'Wind & Hail Deductible must be greater than or equal to all other perils deductible',
          function (value) {
            const state = this.options.context.state;
            const county = this.options.context.home.county?.toUpperCase();
            const windHailInState =
              (!windHailDeductibleCanVaryByCounty[state] ||
                (windHailDeductibleCanVaryByCounty[state] && windHailDeductibleCanVaryByCounty[state][county])) &&
              !noWindHailDeductibleStates[state];

            if (windHailInState) {
              const windHailIsPercent = value < 1;
              const coverageA = this.options.context.homeCoverage?.coverageA;
              const allOtherPerilsIsPercent = this.options.context.homeCoverage?.deductibleAllOther < 1;

              // if value is a %, we need to multiply it by coverage A to see the deductible amount
              const allOtherPerilsAmount = allOtherPerilsIsPercent
                ? this.options.context.homeCoverage?.deductibleAllOther * coverageA
                : this.options.context.homeCoverage?.deductibleAllOther;
              const windHailAmount = windHailIsPercent ? value * coverageA : value;

              return windHailAmount >= allOtherPerilsAmount;
            }
            return true;
          }
        ),
      deductibleAllOther: Yup.number()
        .nullable()
        .test(
          'WindHailPercentageMin',
          'All Other Perils deductible must be a percentage for this state',
          function (value) {
            if (aopDeductibleIsAlwaysPercent[this.options.context?.state] && value > 1) {
              return false;
            }
            return true;
          }
        )
    }),
    scheduledPersonalProperty: Yup.object()
      .shape({
        items: Yup.array(Yup.object()),
        deductible: Yup.mixed().test('deductible', 'SPP Deductible is required', function (value) {
          return !(this.parent.items && this.parent.items.length && !value);
        })
      })
      .nullable(),
    connectedHome: Yup.object().when('homeCoverage', {
      is: (val) => val.coverageCourseOfConstruction,
      then: Yup.object().test(
        'connectedHome',
        'Active construction. Cannot add Connected Home Discount',
        (val) => val.monitored === 'N'
      ),
      otherwise: connectedHomeValidationSchema(isAdvancedConnectedHome)
    }),
    global: Yup.object().shape({
      affinity: Yup.string()
        .validAffinityCode('global.affinity', { affinityLookups, isAgency: session.isAgency })
        .nullable()
    }),
    people: Yup.array(
      Yup.object().shape({
        firstName: Yup.string().trim().required('First Name is required'),
        lastName: Yup.string().trim().required('Last Name is required')
      })
    ),
    fcraDisclaimer: Yup.boolean().test(
      'true-when-adding-coapp',
      'Must agree to FCRA disclaimer when adding co-applicant',
      function (value) {
        const newCoAppAdded = hasNewCoApplicant(
          this.options.context?.initialValues?.people,
          this.options.context?.people
        );

        if (newCoAppAdded) {
          return value;
        }

        // force pass because no coapp got added
        return true;
      }
    )
  });

  return baseSchema.concat(umbrellaValidationSchema({ policy, policies, savedSegment, autoPolicyDetails }));
};
